import { useState } from 'react';
import type { FormikValidationResult } from 'types';
import { collectError, trimValue } from 'utils';

import { forgotPasswordToCXAuthRequest } from '../../../../api/cx';
import { forgotPasswordToFXRequest } from '../../../../api/fx';
import { hasErrors } from '../../../../common/utils/errors';
import ForgotPasswordForm from '../../components/ForgotPasswordForm';
import { forgotPasswordFieldNames } from '../../shared/constants';
import { validateForgotPassword } from '../../shared/validateForgotPassword';

const ForgotPasswordFormContainer: React.FC = () => {
    const [formError, setFormError] = useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState<Record<string, FormikValidationResult>>({});
    const [passwordHadBeenSent, setPasswordHadBeenSent] = useState(false);

    async function onSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (hasErrors(errors)) {
            return;
        }

        const form = event.target as HTMLFormElement;
        const input = form.elements.namedItem(forgotPasswordFieldNames.login) as HTMLInputElement | null;
        const value = trimValue(input?.value ?? '');
        const validationResult = validateForgotPassword(value);

        if (validationResult) {
            return setErrors({ [forgotPasswordFieldNames.login]: validationResult });
        }

        try {
            setIsSubmitting(true);
            let errorMessage = '';
            const { beta, message } = await forgotPasswordToCXAuthRequest(value);

            if (message) {
                errorMessage = message;
            }

            if (beta) {
                const { message } = await forgotPasswordToFXRequest(value);

                if (message) {
                    errorMessage = message;
                }
            }

            if (errorMessage) {
                throw new Error(errorMessage);
            }

            setPasswordHadBeenSent(true);
        } catch (error: unknown) {
            if (error instanceof Error) {
                setFormError(error.message);
            } else if (typeof error === 'string') {
                setFormError(error);
            } else {
                setFormError(undefined);
            }
            collectError(error);
        } finally {
            setIsSubmitting(false);
        }
    }

    return (
        <ForgotPasswordForm
            errors={errors}
            setErrors={setErrors}
            onSubmit={onSubmit}
            isSubmitting={isSubmitting}
            formError={formError}
            passwordHadBeenSent={passwordHadBeenSent}
        />
    );
};

export default ForgotPasswordFormContainer;
